// UserService: All user-related Supabase queries
import { supabase } from '../supabaseClient';
import axios from 'axios';

export interface User {
    id: string;
    email: string;
    name: string;
    full_name?: string;
    role: string;
    kelas?: string;
    angkatan?: number;
    phone?: string;
    instagram?: string;
    target_university_1?: string;
    target_major_1?: string;
    target_university_2?: string;
    target_major_2?: string;
    created_at: string;
    email_confirmed_at?: string;
    // Extended profile data
    profile?: {
        full_name?: string;
        kelas?: string;
        angkatan?: number;
        phone?: string;
        instagram?: string;
        target_university_1?: string;
        target_major_1?: string;
        target_university_2?: string;
        target_major_2?: string;
    };
}

export const UserService = {
    // Get all users from users table with extended profile data (JOIN + paginasi)
    async getAllUsers({ limit = 100, offset = 0 } = {}) {
        // Use RPC to get email_confirmed_at from auth.users
        const { data, error } = await supabase
            .rpc('get_users_admin');

        if (error) {
            console.warn('[UserService] get_users_admin rpc failed, falling back to basic users table:', error);
            // Fallback to basic table if RPC doesn't exist yet
            const { data: fallbackData, error: fallbackError } = await supabase
                .from('users')
                .select('*')
                .order('created_at', { ascending: false })
                .range(offset, offset + limit - 1);

            if (fallbackError) throw fallbackError;
            return fallbackData || [];
        }

        return (data || []).map((u: any) => ({
            ...u,
            full_name: u.full_name || u.name || u.email?.split('@')[0] || 'Unknown User',
            role: u.role || 'user'
        }));
    },

    // Force verify email via RPC
    async forceVerifyEmail(userId: string) {
        const { data, error } = await supabase
            .rpc('admin_verify_email', { user_id: userId });

        if (error) throw error;
        return data;
    },

    // Resend verification via Admin API
    async resendVerificationAdmin(email: string) {
        const response = await axios.post('/api/admin/manageUser', {
            action: 'resendVerification',
            email
        });
        return response.data;
    },

    // Update user in users table (trigger will sync role to user_profiles)
    async updateUser(id: string, updates: Partial<User>) {
        const { error, count } = await supabase
            .from('users')
            .update({
                name: updates.full_name || updates.name,
                role: updates.role,
                full_name: updates.full_name,
                kelas: updates.kelas,
                angkatan: updates.angkatan,
                phone: updates.phone,
                instagram: updates.instagram,
                target_university_1: updates.target_university_1,
                target_major_1: updates.target_major_1,
                target_university_2: updates.target_university_2,
                target_major_2: updates.target_major_2,
            })
            .eq('id', id)
            .select();
        if (error) {
            console.error('[UserService] updateUser error:', error);
            throw error;
        }
        console.log('[UserService] updateUser result - rows affected:', count);
    },

    // Update user role in users table
    async updateUserRole(id: string, role: string) {
        const { data, error } = await supabase
            .from('users')
            .update({ role })
            .eq('id', id)
            .select();
        if (error) {
            console.error('[UserService] updateUserRole error:', error);
            throw error;
        }
        if (!data || data.length === 0) {
            console.error('[UserService] updateUserRole: 0 rows affected - likely RLS policy blocking update');
            throw new Error('Update gagal: tidak ada baris yang berubah. Kemungkinan RLS policy memblokir update ini.');
        }
        console.log('[UserService] updateUserRole success:', data);
    },

    // Delete user from auth and public tables (full cleanup)
    async deleteUser(id: string) {
        const response = await axios.post('/api/admin/manageUser', {
            action: 'deleteUser',
            userId: id
        });
        return response.data;
    },
};
